import { ExtractComponentProp } from '@approvalmax/types';
import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { getCompanyById } from 'modules/common/selectors/companySelectors';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useIsOcrUploadButtonVisible } from 'pages/requestForm/hooks/useIsOcrUploadButtonVisible';
import { memo, useCallback } from 'react';
import {
    ComponentErrorBoundary,
    QBooksMatchingInfo,
    QBooksMatchingPopup,
    QBooksMatchingPopupOld,
} from 'shared/components';
import { useUserBetaFeatures } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';

import { changeQbooksMemo, uploadAttachments } from '../../../actions';
import { AttachmentsSection, MemoSection, RequesterInstructionSection } from '../../../components';
import FileDropArea from '../../../components/FileDropArea';
import FileDropProvider from '../../../components/FileDropProvider';
import { useOcrStatus } from '../../../hooks/useOcrStatus';
import { getContext, getRequest } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { OcrUploadButton } from '../../xero/OcrUploadButton/OcrUploadButton';
import { QBooksAccountLineItemsSection as QBooksAccountLineItemsSectionLegacy } from '../(legacy)/QBooksAccountLineItemsSection/QBooksAccountLineItemsSection';
import { QBooksFooterLegacy } from '../(legacy)/QBooksFooterLegacy/QBooksFooterLegacy';
import { QBooksAccountLineItemsSection } from '../QBooksAccountLineItemsSection/QBooksAccountLineItemsSection';
import { QBooksLineItemsSection } from '../QBooksLineItemsSection/QBooksLineItemsSection';
import { QBooksRequestItemsSummary } from '../QBooksRequestItemsSummary/QBooksRequestItemsSummary';
import { QBooksVendorSection } from '../QBooksVendorSection/QBooksVendorSection';
import { QBooksBillFieldsSection } from './components/QBooksBillFieldsSection/QBooksBillFieldsSection';
import { useQbooksBillFormQbooksConditions, useQbooksBillFormQbooksContext } from './QBooksBillForm.hooks';
import { messages } from './QBooksBillForm.messages';
import { Content, InnerContent, Root, Toolbar, ToolbarHeader, ToolbarHeaderText } from './QBooksBillForm.styles';

const { qBooksConstants } = constants;

interface QBooksBillFormProps {
    className?: string;
}

export const QBooksBillForm = memo<QBooksBillFormProps>((props) => {
    const { className } = props;

    const dispatch = useDispatch();

    const context = useSelector(getContext);
    const request = useSelector<domain.QBooksBillRequest>(getRequest);
    const company = useSelector((state) => getCompanyById(state, request.companyId));
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    const {
        isAccountBasedLinesEnabled,
        isItemBasedLinesEnabled,
        handleMatchingRelatedFieldChange,
        changeLineMatching,
    } = useQbooksBillFormQbooksConditions(useQbooksBillFormQbooksContext());

    const handleUploadAttachments = useCallback(
        (files: File[]) => {
            dispatch(uploadAttachments({ files }));
        },
        [dispatch]
    );

    const handleMatch = useCallback<ExtractComponentProp<typeof QBooksMatchingPopupOld, 'onMatch'>>(
        async ({ lineItemId, matchedLineItemId }, isAccountLine) => {
            changeLineMatching(lineItemId, matchedLineItemId, isAccountLine);
        },
        [changeLineMatching]
    );

    const handleRemoveMatching = useCallback<ExtractComponentProp<typeof QBooksMatchingInfo, 'onRemoveMatching'>>(
        async (lineItemId, isAccountLine) => {
            changeLineMatching(lineItemId, undefined, isAccountLine);
        },
        [changeLineMatching]
    );

    useOcrStatus(request);

    const isOcrUploadButtonVisible = useIsOcrUploadButtonVisible(company, request);

    const {
        isBelgradeUIFilesUploadSection,
        isBelgradeUILineItemsTotalSection,
        isBelgradeUIQBooksMatchingPopup,
        isBelgradeUIQBooksAccountLineItemsSection,
    } = useUserBetaFeatures();

    const onChangeMemo = useCallback(
        (value: string) => {
            dispatch(changeQbooksMemo(value));
        },
        [dispatch]
    );

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider
                    className={className}
                    onUpload={handleUploadAttachments}
                    maxSize={context.attachmentMaxSize}
                    allowedFileExtensions={qBooksConstants.QBO_ALLOWED_FILE_EXTENSIONS}
                >
                    {children}

                    <FileDropArea />
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarHeader>
                        <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                        <ActionsBlock />
                    </ToolbarHeader>
                </Toolbar>

                <Content>
                    <InnerContent>
                        <RequesterInstructionSection instructionText={context.requesterInstruction} />

                        <QBooksVendorSection onChange={handleMatchingRelatedFieldChange} />

                        <QBooksBillFieldsSection onChange={handleMatchingRelatedFieldChange} />

                        {isAccountBasedLinesEnabled && (
                            <ComponentErrorBoundary>
                                {isBelgradeUIQBooksAccountLineItemsSection ? (
                                    <QBooksAccountLineItemsSection
                                        onChange={handleMatchingRelatedFieldChange}
                                        onRemoveMatching={handleRemoveMatching}
                                    />
                                ) : (
                                    <QBooksAccountLineItemsSectionLegacy
                                        onChange={handleMatchingRelatedFieldChange}
                                        onRemoveMatching={handleRemoveMatching}
                                    />
                                )}
                            </ComponentErrorBoundary>
                        )}

                        {isItemBasedLinesEnabled && (
                            <ComponentErrorBoundary>
                                <QBooksLineItemsSection
                                    onChange={handleMatchingRelatedFieldChange}
                                    onRemoveMatching={handleRemoveMatching}
                                />
                            </ComponentErrorBoundary>
                        )}

                        {isBelgradeUILineItemsTotalSection ? <QBooksRequestItemsSummary /> : <QBooksFooterLegacy />}

                        <MemoSection
                            defaultValue={request.details?.memo || ''}
                            disabled={ocrIsLoading}
                            maxLength={constants.qBooksConstants.MEMO_MAX_LENGTH}
                            onChange={onChangeMemo}
                        />

                        {isBelgradeUIFilesUploadSection ? (
                            <Grid gridTemplateColumns='50%' gap={16}>
                                <AttachmentsSection
                                    request={request}
                                    extensions={qBooksConstants.qboAllowedFileExtensions}
                                />
                            </Grid>
                        ) : (
                            <>
                                <FilesSection />

                                {isOcrUploadButtonVisible && <OcrUploadButton request={request} />}
                            </>
                        )}
                    </InnerContent>
                </Content>

                {isBelgradeUIQBooksMatchingPopup ? (
                    <QBooksMatchingPopup
                        requestId={request.id}
                        vendorId={request.details.vendor?.id}
                        billDate={request.details.date}
                        onMatch={handleMatch}
                    />
                ) : (
                    <QBooksMatchingPopupOld
                        requestId={request.id}
                        vendorId={request.details.vendor?.id}
                        billDate={request.details.date}
                        onMatch={handleMatch}
                    />
                )}
            </Root>
        </ConditionWrapper>
    );
});

QBooksBillForm.displayName = 'QBooksBillForm';
